<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const faqs = ref([])
const loading = ref(true)
const searchQuery = ref('')
const activeCategory = ref('ทั้งหมด')
const openFaqId = ref(null)

// Load FAQs
const loadFaqs = async () => {
  loading.value = true
  try {
    const res = await fetch('/api/faqs')
    const data = await res.json()
    if (data.success) {
      faqs.value = data.data
      injectFaqSchema()
    }
  } catch (error) {
    console.error('Failed to load FAQs:', error)
  } finally {
    loading.value = false
  }
}

// Extract dynamic categories from existing FAQs
const categories = computed(() => {
  const list = ['ทั้งหมด']
  faqs.value.forEach(faq => {
    if (faq.category && !list.includes(faq.category)) {
      list.push(faq.category)
    }
  })
  return list
})

// Filtered FAQs based on search query and active category
const filteredFaqs = computed(() => {
  return faqs.value.filter(faq => {
    const matchesCategory = activeCategory.value === 'ทั้งหมด' || faq.category === activeCategory.value
    const matchesSearch = 
      faq.question.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.value.toLowerCase())
    return matchesCategory && matchesSearch
  })
})

const toggleFaq = (id) => {
  if (openFaqId.value === id) {
    openFaqId.value = null
  } else {
    openFaqId.value = id
  }
}

// SEO FAQ Schema Injection
let schemaScript = null
const injectFaqSchema = () => {
  if (schemaScript) {
    schemaScript.remove()
  }

  const publishedFaqs = faqs.value.filter(f => f.is_published)
  if (publishedFaqs.length === 0) return

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': publishedFaqs.map(faq => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer
      }
    }))
  }

  schemaScript = document.createElement('script')
  schemaScript.type = 'application/ld+json'
  schemaScript.innerHTML = JSON.stringify(schema)
  document.head.appendChild(schemaScript)
}

onMounted(() => {
  loadFaqs()
})

onUnmounted(() => {
  if (schemaScript) {
    schemaScript.remove()
  }
})
</script>

<template>
  <div class="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50/50 pt-32 pb-16 px-4 sm:px-6 lg:px-8 sm:pt-36 lg:pt-40">
    <div class="max-w-4xl mx-auto">
      
      <!-- Hero Section -->
      <div class="text-center mb-16">
        <span class="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold text-emerald-700 bg-emerald-50 rounded-full border border-emerald-100 mb-4">
          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
          ศูนย์ช่วยเหลือ & ตอบคำถาม
        </span>
        <h1 class="text-4xl sm:text-5xl font-black text-slate-950 tracking-tight leading-tight">
          คำถามที่พบบ่อย <span class="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">(FAQ)</span>
        </h1>
        <p class="mt-4 text-base sm:text-lg text-slate-500 max-w-2xl mx-auto font-medium">
          ค้นหาคำตอบสำหรับข้อสงสัยทั่วไปเกี่ยวกับบ้านเก็บของพรีเมียม โรงเรือน การติดตั้ง การชำระเงิน และการรับประกันของ Morespace
        </p>
      </div>

      <!-- Search and Filters -->
      <div class="mb-10 space-y-6">
        <!-- Search Input -->
        <div class="relative max-w-xl mx-auto">
          <input 
            v-model="searchQuery" 
            type="text" 
            placeholder="ค้นหาหัวข้อหรือคำถามที่คุณต้องการ..." 
            class="w-full pl-12 pr-4 py-4 rounded-2xl border border-slate-200/80 bg-white shadow-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-medium"
          />
          <div class="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
          </div>
        </div>

        <!-- Categories Filters -->
        <div class="flex flex-wrap justify-center gap-2">
          <button 
            v-for="cat in categories" 
            :key="cat" 
            @click="activeCategory = cat"
            :class="[
              'px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-200 border shadow-sm',
              activeCategory === cat 
                ? 'bg-slate-900 border-slate-900 text-white shadow-slate-900/10' 
                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-350 hover:bg-slate-50'
            ]"
          >
            {{ cat }}
          </button>
        </div>
      </div>

      <!-- FAQ Accordion List -->
      <div v-if="loading" class="flex flex-col justify-center items-center py-20">
        <div class="relative w-12 h-12">
          <div class="absolute inset-0 rounded-full border-t-2 border-emerald-600 animate-spin"></div>
        </div>
        <p class="mt-4 text-sm font-semibold text-slate-500">กำลังโหลดคำถามพบบ่อย...</p>
      </div>

      <div v-else-if="filteredFaqs.length === 0" class="text-center py-20 bg-white rounded-3xl border border-slate-200/60 shadow-sm p-8">
        <div class="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
          <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"></path></svg>
        </div>
        <h3 class="text-lg font-bold text-slate-900">ไม่พบข้อมูลคำถามที่ตรงกับการค้นหา</h3>
        <p class="text-sm text-slate-500 mt-1">ลองเปลี่ยนคำค้นหาหรือเลือกหมวดหมู่อื่นแทน</p>
      </div>

      <div v-else class="space-y-4">
        <div 
          v-for="faq in filteredFaqs" 
          :key="faq.id" 
          class="bg-white rounded-2xl border border-slate-200/70 hover:border-emerald-500/30 shadow-sm overflow-hidden transition-all duration-300"
          :class="{ 'ring-1 ring-emerald-500/20 border-emerald-500/30 shadow-md': openFaqId === faq.id }"
        >
          <!-- Header (Question) -->
          <button 
            @click="toggleFaq(faq.id)" 
            class="w-full flex items-center justify-between p-5 text-left focus:outline-none"
          >
            <span class="font-bold text-slate-950 pr-4 text-base sm:text-lg hover:text-emerald-600 transition-colors">
              {{ faq.question }}
            </span>
            <div class="flex items-center shrink-0">
              <span class="text-xs font-bold text-slate-400 bg-slate-50 border border-slate-100 rounded-md px-2 py-1 mr-3 uppercase">
                {{ faq.category }}
              </span>
              <svg 
                class="w-5 h-5 text-slate-400 transition-transform duration-300" 
                :class="{ 'rotate-180 text-emerald-500': openFaqId === faq.id }" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor" 
                stroke-width="2.5"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
          </button>

          <!-- Body (Answer) -->
          <div 
            class="transition-all duration-300 ease-in-out"
            :style="{ maxHeight: openFaqId === faq.id ? '500px' : '0px' }"
          >
            <div class="p-5 pt-0 border-t border-slate-100 bg-slate-50/50 text-slate-600 text-sm sm:text-base leading-relaxed whitespace-pre-line font-medium">
              {{ faq.answer }}
            </div>
          </div>
        </div>
      </div>

      <!-- Call to Action -->
      <div class="mt-16 text-center bg-gradient-to-br from-slate-900 to-slate-950 text-white rounded-3xl p-8 sm:p-12 shadow-xl border border-slate-800 relative overflow-hidden">
        <div class="absolute -right-10 -bottom-10 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div class="absolute -left-10 -top-10 w-40 h-40 bg-emerald-500/10 rounded-full blur-3xl"></div>
        
        <h3 class="text-2xl font-black mb-3">ยังไม่พบคำตอบที่คุณต้องการใช่ไหม?</h3>
        <p class="text-slate-400 max-w-xl mx-auto mb-6 text-sm sm:text-base">
          หากคุณมีคำถามเพิ่มเติมหรือต้องการคำปรึกษาพิเศษ ทีมงานผู้เชี่ยวชาญของเราพร้อมให้บริการตอบคำถามและช่วยเหลือคุณเสมอ
        </p>
        <div class="flex flex-wrap justify-center gap-4">
          <router-link to="/contact" class="px-6 py-3 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/20">
            ติดต่อฝ่ายบริการลูกค้า
          </router-link>
          <router-link to="/quotation" class="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl border border-slate-700 transition-all">
            ขอใบเสนอราคา
          </router-link>
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
.transition-all {
  transition-property: all;
}
</style>
